import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  CheckCircleRounded,
  ClearRounded,
  CloudUploadRounded,
  DoneRounded,
  GetAppRounded,
  LaunchRounded,
  LinkRounded
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import moment from 'moment';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { getProjectDocumentRevisionCoversheets } from '../../api/project-document-revisions';
import { UserApprovalWithFile } from '../../api/user-approvals';
import { RootState } from '../../app/store';
import ApproveChip from '../../components/ApproveChip';
import { bellNotificationActionItemParams } from '../../components/BellNotification';
import DocRevisionInfoCard from '../../components/DocRevisionInfoCard';
import DocumentCoversheetFile from '../../components/DocumentCoversheetFile';
import DocumentFile from '../../components/DocumentFile';
import DropzoneArea from '../../components/DropzoneArea';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import RevisionNote from '../../components/RevisionNote';
import { ENABLE_ANNOTATION } from '../../constants';
import checkWebUrl from '../../functions/checkWebUrl';
import getIconFile from '../../functions/getIconFile';
import handleDropRejected from '../../functions/handleDropRejected';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useDropzoneArea from '../../hooks/useDropzoneArea';
import { getBellNotificationActionItemsAsync } from '../BellNotification/bellNotificationSlide';
import Breadcrumb from '../Breadcrumb';
import { getChecklistCreatorFilesAsync } from '../ChecklistCreatorFile/checklistCreatorFileSlice';
import { getChecklistUserApprovalFilesAsync } from '../ChecklistUserApprovalFile/checklistUserApprovalFileSlice';
import { getCommentFileAsync } from '../CommentFile/commentFileSlice';
import loadingSlice from '../Loading/loadingSlice';
import { getApprovalCodesAsync } from '../MasterData/ApprovalCode/approvalCodeSlice';
import {
  ApprovalType,
  getApprovalTypesAsync
} from '../MasterData/ApprovalType/approvalTypeSlice';
import { WorkflowStatusLabel } from '../ProjectDocumentRevision/DocumentRevisionView';
import { submitCommentApprovalAsync } from '../UserApproval/userApprovalSlice';
import { getProjectDocumentWorkflowAsync } from './projectDocumentWorkflowSlice';
import { getUsersAsync } from '../Project/plugAndPlayUsersSlice';

enum ApprovalCodeLabel {
  Acknowledge = 4
}

export enum ApprovalTypeLabel {
  Approve = 1,
  Reject = 2,
  Acknowledge = 3
}

enum FileTypeLabel {
  Comment = 1,
  Checklist = 2
}

enum DownloadFileLabel {
  Creator = 1,
  UserApproval = 2
}

let inputKey = 0;

export default function DocumentWorkflowApproval() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dropzoneArea = useRef<HTMLDivElement | null>(null);
  const { inDropzone } = useDropzoneArea(dropzoneArea);

  const { user } = useSelector((state: RootState) => state.userProfile);
  const { data: users } = useSelector(
    (state: RootState) => state.plugAndPlayUsers
  );

  const { data: approvalCodes } = useSelector(
    (state: RootState) => state.approvalCodes
  );
  const { data: approvalTypes } = useSelector(
    (state: RootState) => state.approvalTypes
  );
  const { selectedProjectDocumentWorkflow } = useSelector(
    (state: RootState) => state.projectDocumentWorkflows
  );
  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  useEffect(() => {
    if (users.length === 0) {
      dispatch(getUsersAsync());
    }
  }, [dispatch, users]);

  const coversheetName = `${selectedProjectDocument?.no}_${selectedProjectDocumentRevision?.revision.name}_${selectedProjectDocument?.name}.pdf`;

  const documentRevisionFiles =
    selectedProjectDocumentRevision?.projectDocumentRevisionFiles ?? [];
  const documentWorkflowSteps =
    selectedProjectDocumentWorkflow?.projectDocumentWorkflowSteps ?? [];
  const workflowStatusId = selectedProjectDocumentWorkflow?.workflowStatusId;
  const documentCreator = users.find(
    (user) =>
      user.id ===
      (selectedProjectDocumentWorkflow?.creatorId ??
        selectedProjectDocument?.documentCreatorId)
  );

  const params = useParams<{ docFlowId: string }>();
  const docFlowId = Number.parseInt(params.docFlowId, 10);

  const isDocCreator =
    (selectedProjectDocumentWorkflow?.creatorId === user?.id ||
      selectedProjectDocument?.documentCreatorId === user?.id) ??
    false;
  const isFinalApprover =
    selectedProjectDocumentWorkflow?.currentWorkflowStep.workflowStep
      .isFinalApprover;
  const needChecklist =
    selectedProjectDocumentWorkflow?.currentWorkflowStep.workflowStep
      .isChecklist;
  const responseAnswer =
    user &&
    documentWorkflowSteps
      .find(
        (documentWorkflowStep) =>
          documentWorkflowStep.id ===
          selectedProjectDocumentWorkflow?.currentWorkflowStepId
      )
      ?.userApprovals.find((userApproval) => userApproval.userId === user.id);
  const workflowStepOfThisUser =
    user &&
    documentWorkflowSteps.find((documentWorkflowStep) => {
      const thisUser = documentWorkflowStep.userApprovals.find(
        (userApproval) => userApproval.userId === user.id
      );
      if (thisUser) return documentWorkflowStep;
    })?.workflowStep; //TODO: Delete and revise after adding internal/external user

  const isIFIPurpose =
    selectedProjectDocumentRevision?.purpose.abbreviation === 'IFI';
  const hasFileOrLink =
    documentRevisionFiles.length > 0 ||
    (selectedProjectDocumentRevision &&
      selectedProjectDocumentRevision.linkUrl);

  const MAX_SIZE_IN_MB = Number.parseInt(
    process.env.REACT_APP_COMMON_FILE_SIZE || '25',
    10
  );
  const { getRootProps, getInputProps } = useDropzone(
    dropzoneOptions(FileTypeLabel.Comment)
  );
  const {
    getRootProps: getRootChecklistProps,
    getInputProps: getInputChecklistProps
  } = useDropzone(dropzoneOptions(FileTypeLabel.Checklist));

  const [userApproval, setUserApproval] = useState<UserApprovalWithFile>({
    id: null,
    approvalTypeId: null,
    comment: null,
    approvalCode: null,
    isChecked: false,
    checklistLinkUrl: null,
    files: []
  });

  const [successCopied, setSuccessCopied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const commentFiles = getFiles(FileTypeLabel.Comment);
  const checklistFiles = getFiles(FileTypeLabel.Checklist);

  const isCheckedCreator =
    selectedProjectDocumentWorkflow?.checklistCreator?.isChecked ?? false;
  const checklistCreatorLink =
    selectedProjectDocumentWorkflow?.checklistCreator?.linkUrl ?? null;
  const checklistCreatorFiles =
    selectedProjectDocumentWorkflow?.checklistCreator?.checklistCreatorFiles ??
    [];
  const checklistCreatorFileLabel = `${
    checklistCreatorFiles.length
  } ${t('projectDocumentWorkflowApproval.labels.checklistFile')}${checklistCreatorFiles.length > 1 ? 's' : ''}`;
  const showCreatorContent = isDocCreator || workflowStepOfThisUser?.isInternal;

  const hasPermission =
    isDocCreator ||
    (
      selectedProject?.users.filter(
        ({ accessControls, userId }) =>
          userId === user?.id &&
          accessControls.some(
            ({ id }) => id === selectedProjectDocument?.accessControlId
          )
      ) ?? []
    ).length > 0 ||
    (
      selectedProjectDocument?.projectDocumentCustomAccessControls.filter(
        ({ userId }) => userId === user?.id
      ) ?? []
    ).length > 0;

  useEffect(() => {
    const [defaultApprovalType] = approvalTypes.filter((approvalType) =>
      getApprovalType(approvalType)
    );
    const defaultAcknowledge = approvalCodes.find(
      (approvalCode) => approvalCode.id === ApprovalCodeLabel.Acknowledge
    );
    setUserApproval({
      ...userApproval,
      approvalCode:
        isFinalApprover && isIFIPurpose && defaultAcknowledge
          ? defaultAcknowledge
          : null,
      approvalTypeId: defaultApprovalType?.id
    });
  }, [selectedProjectDocumentRevision, approvalTypes]);

  useEffect(() => {
    dispatch(getProjectDocumentWorkflowAsync(docFlowId));
  }, [docFlowId]);

  useEffect(() => {
    if (approvalCodes.length === 0) dispatch(getApprovalCodesAsync());
    if (approvalTypes.length === 0) dispatch(getApprovalTypesAsync());
  }, []);

  function getFiles(fileType: FileTypeLabel): FileWithPath[] {
    return userApproval.files
      .filter(({ fileTypeId }) => fileTypeId === fileType)
      .map(({ file }) => file);
  }

  function dropzoneOptions(fileType: FileTypeLabel) {
    return {
      onDropAccepted: onDropAccepted(fileType),
      onDropRejected: (fileRejections: FileRejection[]) =>
        handleDropRejected(fileRejections, dispatch, MAX_SIZE_IN_MB),
      onDrop: () => inputKey++, // To reset input every time any action occurs
      // To select type of upload file add 'accept' property e.g. accept: '.pdf'
      maxSize: MAX_SIZE_IN_MB * 1048576, //maxSize per file is 25 MB
      multiple: true
    };
  }

  function fileList(files: FileWithPath[], fileType: FileTypeLabel): ReactNode {
    return files.map((file, index) => {
      const fileSizeInKB = Math.round(file.size / 1024);
      const fileSizeInMB = Math.round((file.size * 10) / 1048576) / 10;
      const fileSize =
        file.size > 1048576 ? `${fileSizeInMB}MB` : `${fileSizeInKB}KB`;
      return (
        <div className={classes.flexAlignCenter} key={index}>
          {getIconFile(file.path as string)}
          <Typography
            className={clsx(classes.filename, classes.marginLR)}
            variant="body2"
          >
            {`${file.path} (${fileSize})`}
          </Typography>
          <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.remove') as string} arrow>
            <IconButton
              size="small"
              onClick={() => onFilterOut(file.path as string, fileType)}
            >
              <ClearRounded />
            </IconButton>
          </Tooltip>
        </div>
      );
    });
  }

  function onDropAccepted(dropFileType: FileTypeLabel) {
    return function (acceptedFiles: FileWithPath[]) {
      const previousFiles = userApproval.files.filter(
        ({ file, fileTypeId }) =>
          !acceptedFiles.some(
            (acceptedFile) =>
              acceptedFile.path === file.path && fileTypeId == dropFileType
          )
      );
      const formattedFile = acceptedFiles.map((file) => ({
        file,
        fileTypeId: dropFileType
      }));
      setUserApproval({
        ...userApproval,
        files: [...previousFiles, ...formattedFile]
      });
    };
  }

  function onFilterOut(path: string, fileType: FileTypeLabel) {
    const newFiles = userApproval.files.filter(
      ({ file, fileTypeId }) => !(file.path == path && fileTypeId == fileType)
    );
    setUserApproval({
      ...userApproval,
      files: newFiles
    });
  }

  function getApprovalType(approvalType: ApprovalType) {
    if (isIFIPurpose) {
      return approvalType.id === ApprovalTypeLabel.Acknowledge;
    } else {
      return userApproval.approvalCode
        ? userApproval.approvalCode.approvalTypes.find(
            (approvalTypeInComment) =>
              approvalTypeInComment.id === approvalType.id
          )
        : approvalType.id !== ApprovalTypeLabel.Acknowledge;
    }
  }

  function getSeverity(id: number) {
    if (id === WorkflowStatusLabel.Completed) {
      return 'success';
    }
    if (id === WorkflowStatusLabel.Rejected) {
      return 'error';
    }
    return 'warning';
  }

  function handleCopyLink() {
    setSuccessCopied(true);
    navigator.clipboard.writeText(
      `${window.location.origin}${window.location.pathname}`
    );
    setTimeout(() => {
      setSuccessCopied(false);
    }, 2000);
  }

  async function downloadCommentFile({
    commentFileId,
    contentType,
    filename
  }: {
    commentFileId: number;
    contentType: string;
    filename: string;
  }) {
    await dispatch(
      getCommentFileAsync({
        commentFileId,
        contentType,
        filename
      })
    ).unwrap();
    dispatch(
      loadingSlice.actions.success({
        message: t('projectDocumentWorkflowApproval.messages.fileDownloadedSuccessfully') as string
      })
    );
  }

  async function downloadChecklistFiles(
    files: { id: number; name: string }[],
    downloadFile: DownloadFileLabel
  ) {
    function getFilename(files: { id: number; name: string }[]) {
      if (files.length === 1) {
        return files[0].name;
      } else if (files.length > 1) {
        return `quality-checklist_${moment().format('YYYYMMDDhhmmss')}.zip`;
      }
      return;
    }

    const filename = getFilename(files);
    if (filename) {
      const ids = files.map((file) => file.id);

      if (downloadFile === DownloadFileLabel.Creator) {
        await dispatch(
          getChecklistCreatorFilesAsync({ ids, filename })
        ).unwrap();
      } else if (downloadFile === DownloadFileLabel.UserApproval) {
        await dispatch(
          getChecklistUserApprovalFilesAsync({ ids, filename })
        ).unwrap();
      }
      dispatch(
        loadingSlice.actions.success({
          message: `${
            files.length > 1 ? t('projectDocumentWorkflowApproval.messages.filesWere') : t('projectDocumentWorkflowApproval.messages.fileWas')
          } ${t('projectDocumentWorkflowApproval.messages.downloadedSuccessfully')}`
        })
      );
    }
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (responseAnswer && userApproval.approvalTypeId !== null) {
        const result = await dispatch(
          submitCommentApprovalAsync({
            ...userApproval,
            id: responseAnswer.id,
            approvalTypeId: userApproval.approvalTypeId
          })
        );
        await unwrapResult(result);
        dispatch(
          loadingSlice.actions.success({
            message: t('projectDocumentWorkflowApproval.messages.answerSubmitted') as string
          })
        );
        dispatch(
          getBellNotificationActionItemsAsync(bellNotificationActionItemParams)
        );
        setTimeout(() => {
          history.length > 1 ? history.goBack() : history.push('/my-tasks');
        }, 1000);
      } else {
        if (userApproval.approvalTypeId === null) {
          throw new Error(t('projectDocumentWorkflowApproval.errors.pleaseSelectAnswer') as string);
        } else {
          throw new Error(t('projectDocumentWorkflowApproval.errors.cannotFindName') as string);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      {hasPermission ? (
        <Breadcrumb
          name="approval"
          data={{
            projectName: selectedProject?.name,
            projectId: selectedProject?.id,
            documentName: selectedProjectDocument?.name,
            documentId: selectedProjectDocument?.id,
            revisionName: `Purpose: ${selectedProjectDocumentRevision?.purpose.abbreviation}, Revision: ${selectedProjectDocumentRevision?.revision.name}`,
            revisionId: selectedProjectDocumentRevision?.id,
            flowName: selectedProjectDocumentWorkflow?.name
          }}
        />
      ) : (
        <Breadcrumb
          name="approvalOnly"
          data={{
            projectName: selectedProject?.name,
            projectId: selectedProject?.id,
            flowName: selectedProjectDocumentWorkflow?.name
          }}
        />
      )}
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocumentWorkflowApproval.title')}
        <Tooltip
          title={(successCopied ? t('projectDocumentWorkflowApproval.tooltips.copied') : t('projectDocumentWorkflowApproval.tooltips.copyLink')) as string}
          arrow
        >
          <IconButton
            className={classes.marginIconButton}
            size="small"
            onClick={handleCopyLink}
          >
            {successCopied ? (
              <DoneRounded className={classes.greenColor} />
            ) : (
              <LinkRounded className={classes.rotate45} />
            )}
          </IconButton>
        </Tooltip>
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.clearMarginForMobile}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && selectedProjectDocumentRevision && (
            <DocRevisionInfoCard
              document={selectedProjectDocument}
              docRevision={selectedProjectDocumentRevision}
            />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          {selectedProjectDocumentWorkflow &&
            selectedProjectDocumentWorkflow.workflowStatusId !==
              WorkflowStatusLabel.InProgress && (
              <Alert
                className={classes.marginBottom1}
                severity={getSeverity(
                  selectedProjectDocumentWorkflow.workflowStatusId
                )}
              >
                {`${t('projectDocumentWorkflowApproval.messages.thisWorkflowHasBeen')} ${selectedProjectDocumentWorkflow.workflowStatus.name.toLowerCase()} ${t('projectDocumentWorkflowApproval.messages.at')} ${moment(
                  selectedProjectDocumentWorkflow.finishedDate
                ).format('lll')}`}
              </Alert>
            )}
          {selectedProjectDocumentWorkflow && (
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectDocumentWorkflowApproval.sections.workflowDetails')}
              </Typography>
              <Divider className={classes.divider} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">{t('projectDocumentWorkflowApproval.labels.flowName')}</Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  {selectedProjectDocumentWorkflow.name}
                </Grid>
                {selectedProjectDocumentWorkflow.linkUrl && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2">{t('projectDocumentWorkflowApproval.labels.link')}</Typography>
                    </Grid>
                    <Grid className={classes.linkBox} item xs={12} sm={9}>
                      <Typography
                        className={clsx(classes.link, classes.marginLR)}
                        component="a"
                        href={checkWebUrl(
                          selectedProjectDocumentWorkflow.linkUrl
                        )}
                        target="_blank"
                        variant="body1"
                        display="inline"
                      >
                        {selectedProjectDocumentWorkflow.linkName
                          ? selectedProjectDocumentWorkflow.linkName
                          : selectedProjectDocumentWorkflow.linkUrl}
                        <LaunchRounded color="action" fontSize="small" />
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          )}
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocumentWorkflowApproval.sections.latestFiles')}
            </Typography>
            <Divider className={classes.divider} />
            {documentRevisionFiles &&
            selectedProjectDocumentRevision &&
            (hasPermission || workflowStepOfThisUser) ? (
              <>
                {hasFileOrLink && (
                  <DocumentCoversheetFile
                    coversheetName={coversheetName}
                    onPreview={() =>
                      getProjectDocumentRevisionCoversheets({
                        projectDocumentRevisionId:
                          selectedProjectDocumentRevision.id,
                        filename: coversheetName,
                        isDownload: false,
                        isPreview: true
                      })
                    }
                    onDownload={() =>
                      getProjectDocumentRevisionCoversheets({
                        projectDocumentRevisionId:
                          selectedProjectDocumentRevision.id,
                        filename: coversheetName
                      })
                    }
                  />
                )}
                {documentRevisionFiles.length > 0 ? (
                  <DocumentFile
                    renderDocumentRevisionFiles={documentRevisionFiles}
                    projectDocumentRevisionId={
                      selectedProjectDocumentRevision.id
                    }
                    isDocCreator={isDocCreator}
                    viewAsInternal={workflowStepOfThisUser?.isInternal}
                    disabledModified
                    hideViewVersion
                    accessLevelName={
                      selectedProjectDocument?.accessControl.name
                    }
                    username={
                      user ? `${user.firstName} ${user.lastName}` : undefined
                    }
                    enableAnnotation={
                      ENABLE_ANNOTATION &&
                      !!selectedProject?.projectNo &&
                      (
                        process.env.REACT_APP_PILOT_ANNOTATION_PROJECT_NO?.split(
                          ','
                        ).filter(Boolean) ?? []
                      ).includes(selectedProject.projectNo)
                    }
                  />
                ) : (
                  !selectedProjectDocumentRevision?.linkUrl && (
                    <div className={classes.fileNotFound}>{t('projectDocumentWorkflowApproval.messages.fileNotFound')}</div>
                  )
                )}
                {selectedProjectDocumentRevision.linkUrl && (
                  <div className={classes.linkNoteBox}>
                    <Typography
                      className={classes.link}
                      component="a"
                      href={checkWebUrl(
                        selectedProjectDocumentRevision.linkUrl
                      )}
                      target="_blank"
                      variant="body1"
                      display="inline"
                    >
                      {selectedProjectDocumentRevision.linkName
                        ? selectedProjectDocumentRevision.linkName
                        : selectedProjectDocumentRevision.linkUrl}
                      <LaunchRounded color="action" fontSize="small" />
                    </Typography>
                  </div>
                )}
              </>
            ) : (
              <div className={classes.fileNotFound}>
                {t('projectDocumentWorkflowApproval.messages.needPermission')}
              </div>
            )}
          </Paper>
          {selectedProjectDocumentRevision &&
            selectedProjectDocumentRevision.note && (
              <Paper className={classes.card}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('projectDocumentWorkflowApproval.sections.revisionNote')}
                </Typography>
                <Divider className={classes.divider} />
                <RevisionNote
                  value={selectedProjectDocumentRevision.note}
                  documentRevisionId={selectedProjectDocumentRevision.id}
                />
              </Paper>
            )}
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocumentWorkflowApproval.sections.documentWorkflow')}
            </Typography>
            <Divider className={classes.divider} />
            <div className={classes.flexFlowStep}>
              <Avatar className={classes.smallAvatar}>1</Avatar>
              <Typography className={classes.flexGrowName} variant="subtitle1">
                {t('projectDocumentWorkflowApproval.labels.documentCreator')}
              </Typography>
            </div>
            <div className={clsx(classes.userField, classes.marginBottom2)}>
              <div className={classes.usernameVerified}>
                <Typography className={classes.lineHeight120} variant="h6">
                  {documentCreator
                    ? `${documentCreator.firstName} ${documentCreator.lastName}`
                    : t('projectDocumentWorkflowApproval.labels.backspaceUser')}
                </Typography>
                {showCreatorContent && (
                  <div className={classes.checklistField}>
                    {isCheckedCreator && (
                      <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.qualityChecked') as string} arrow>
                        <CheckCircleRounded className={classes.greenColor} />
                      </Tooltip>
                    )}
                    {checklistCreatorFiles.length > 0 && (
                      <Tooltip
                        title={`${t('projectDocumentWorkflowApproval.tooltips.download')} ${checklistCreatorFileLabel}` as string}
                        arrow
                      >
                        <Chip
                          className={classes.truncatedChip}
                          variant="outlined"
                          size="small"
                          label={checklistCreatorFileLabel}
                          icon={<GetAppRounded />}
                          onClick={() =>
                            downloadChecklistFiles(
                              checklistCreatorFiles,
                              DownloadFileLabel.Creator
                            )
                          }
                        />
                      </Tooltip>
                    )}
                    {checklistCreatorLink && (
                      <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.openChecklistLink') as string} arrow>
                        <Chip
                          className={classes.truncatedChip}
                          component="a"
                          href={checkWebUrl(checklistCreatorLink)}
                          target="_blank"
                          variant="outlined"
                          size="small"
                          label={t('projectDocumentWorkflowApproval.labels.checklistLink')}
                          icon={<LaunchRounded />}
                          onClick={() => {}} // To display clickable chip
                        />
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
            {selectedProjectDocumentWorkflow &&
              documentWorkflowSteps.map((documentWorkflowStep, index) => {
                const showStepDetail =
                  documentWorkflowStep.id <=
                  selectedProjectDocumentWorkflow.currentWorkflowStepId;
                return (
                  <div key={documentWorkflowStep.id}>
                    <div className={classes.flexFlowStep}>
                      <Avatar
                        className={clsx(classes.smallAvatar, {
                          [classes.avatarDisabled]: !showStepDetail
                        })}
                      >
                        {index + 2}
                      </Avatar>
                      <Typography
                        className={classes.flexGrowName}
                        variant="subtitle1"
                        color={showStepDetail ? 'textPrimary' : 'textSecondary'}
                      >
                        {documentWorkflowStep.workflowStep.name}
                      </Typography>
                    </div>
                    {documentWorkflowStep.userApprovals.map((userApproval) => {
                      const isCheckedUserApproval =
                        userApproval.checklistUserApproval?.isChecked ?? false;
                      const checklistUserApprovalLink =
                        userApproval.checklistUserApproval?.linkUrl ?? null;
                      const checklistUserApprovalFiles =
                        userApproval.checklistUserApproval
                          ?.checklistUserApprovalFiles ?? [];
                      const checklistUserApprovalFileLabel = `${
                        checklistUserApprovalFiles.length
                      } ${t('projectDocumentWorkflowApproval.labels.checklistFile')}${
                        checklistUserApprovalFiles.length > 1 ? 's' : ''
                      }`;
                      const showMoreContent = isDocCreator
                        ? (!selectedProjectDocumentWorkflow.isCreatorViewOnlyExternal ||
                            !documentWorkflowStep.workflowStep.isInternal) &&
                          (!selectedProjectDocumentWorkflow.isCreatorViewOnlyFinal ||
                            documentWorkflowStep.workflowStep.isFinalApprover)
                        : workflowStepOfThisUser?.isInternal ||
                          !documentWorkflowStep.workflowStep.isInternal;
                      return (
                        <div
                          className={clsx(
                            classes.userField,
                            classes.marginBottom2
                          )}
                          key={userApproval.id}
                        >
                          <div className={classes.usernameVerified}>
                            <Typography
                              className={classes.lineHeight120}
                              variant="h6"
                            >
                              {userApproval.user
                                ? `${userApproval.user.firstName} ${userApproval.user.lastName}`
                                : `Backspace User: ${userApproval.userId}`}
                            </Typography>
                            {showMoreContent && (
                              <div className={classes.checklistField}>
                                {isCheckedUserApproval && (
                                  <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.qualityChecked') as string} arrow>
                                    <CheckCircleRounded
                                      className={classes.greenColor}
                                    />
                                  </Tooltip>
                                )}
                                {checklistUserApprovalFiles.length > 0 && (
                                  <Tooltip
                                    title={`${t('projectDocumentWorkflowApproval.tooltips.download')} ${checklistUserApprovalFileLabel}` as string}
                                    arrow
                                  >
                                    <Chip
                                      className={classes.truncatedChip}
                                      variant="outlined"
                                      size="small"
                                      label={checklistUserApprovalFileLabel}
                                      icon={<GetAppRounded />}
                                      onClick={() =>
                                        downloadChecklistFiles(
                                          checklistUserApprovalFiles,
                                          DownloadFileLabel.UserApproval
                                        )
                                      }
                                    />
                                  </Tooltip>
                                )}
                                {checklistUserApprovalLink && (
                                  <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.openChecklistLink') as string} arrow>
                                    <Chip
                                      className={classes.truncatedChip}
                                      component="a"
                                      href={checkWebUrl(
                                        checklistUserApprovalLink
                                      )}
                                      target="_blank"
                                      variant="outlined"
                                      size="small"
                                      label={t('projectDocumentWorkflowApproval.labels.checklistLink')}
                                      icon={<LaunchRounded />}
                                      onClick={() => {}} // To display clickable chip
                                    />
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          </div>
                          {showMoreContent && userApproval.comment && (
                            <Typography
                              variant="body2"
                              style={{
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {t('projectDocumentWorkflowApproval.labels.comment')} : {userApproval.comment}
                            </Typography>
                          )}
                          {showMoreContent &&
                            userApproval.commentFiles.length > 0 && (
                              <div className={classes.commentFileList}>
                                <Typography
                                  className={classes.marginRight1}
                                  variant="body2"
                                >
                                  {t('projectDocumentWorkflowApproval.labels.commentFiles')} :
                                </Typography>
                                {userApproval.commentFiles.map(
                                  (file, index) => {
                                    return (
                                      <div
                                        className={classes.marginRight1}
                                        key={file.id}
                                      >
                                        <Typography
                                          className={clsx(
                                            classes.filename,
                                            classes.link
                                          )}
                                          onClick={() =>
                                            downloadCommentFile({
                                              commentFileId: file.id,
                                              contentType: file.s3ContentType,
                                              filename: file.name
                                            })
                                          }
                                          variant="caption"
                                        >
                                          {file.name}
                                        </Typography>
                                        {index + 1 <
                                          userApproval.commentFiles.length && (
                                          <Typography variant="caption">
                                            ,
                                          </Typography>
                                        )}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          {userApproval.approvalCodeId && (
                            <Typography display="block" variant="caption">
                              {
                                approvalCodes.find(
                                  (approvalCode) =>
                                    approvalCode.id ===
                                    userApproval.approvalCodeId
                                )?.name
                              }
                            </Typography>
                          )}
                          {userApproval.autoApprovalNote && (
                            <Typography display="block" variant="caption">
                              {t('projectDocumentWorkflowApproval.labels.note')} : {userApproval.autoApprovalNote}
                            </Typography>
                          )}
                          {(workflowStatusId ===
                            WorkflowStatusLabel.InProgress ||
                            userApproval.approvalTypeId) && (
                            <ApproveChip
                              approvalTypeId={userApproval.approvalTypeId}
                              submittedDate={userApproval.submittedDate}
                              dueDate={documentWorkflowStep.dueDate}
                              isAutoApproval={Boolean(
                                userApproval.isAutoApproval
                              )}
                              disabled={!showStepDetail}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </Paper>
          <form onSubmit={onSubmit}>
            {responseAnswer &&
              !responseAnswer.approvalTypeId &&
              needChecklist &&
              workflowStatusId === WorkflowStatusLabel.InProgress && (
                <Paper className={classes.checklistCard}>
                  <div className={classes.padding2}>
                    <div className={classes.flexAlignCenter}>
                      <Typography
                        className={classes.flexGrow}
                        variant="subtitle1"
                      >
                        {t('projectDocumentWorkflowApproval.sections.qualityCheck')}
                      </Typography>
                      <input
                        key={inputKey}
                        {...getInputChecklistProps()}
                        id="upload-checklist-file"
                        disabled={loading}
                      />
                      <label htmlFor="upload-checklist-file">
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<CloudUploadRounded />}
                        >
                          {t('projectDocumentWorkflowApproval.buttons.uploadChecklist')}
                        </Button>
                      </label>
                    </div>
                    <div className={classes.flexDivider}>
                      <Divider className={classes.divider} />
                      <Typography
                        className={classes.marginLR}
                        variant="caption"
                        color="textSecondary"
                      >
                        {t('projectDocumentWorkflowApproval.labels.maximumSize')}: {MAX_SIZE_IN_MB} MB/{t('projectDocumentWorkflowApproval.labels.file')}
                      </Typography>
                    </div>
                    <div className={classes.checklistFile}>
                      {checklistFiles.length === 0 && (
                        <div className={classes.notFoundChecklistFile}>
                          <Typography>{t('projectDocumentWorkflowApproval.messages.dropFilesHere')}</Typography>
                          <Typography color="textSecondary" variant="caption">
                            {t('projectDocumentWorkflowApproval.messages.orUseThe')} <b>{t('projectDocumentWorkflowApproval.buttons.uploadChecklist').toUpperCase()}</b> {t('projectDocumentWorkflowApproval.messages.button')}
                          </Typography>
                        </div>
                      )}
                      {fileList(checklistFiles, FileTypeLabel.Checklist)}
                    </div>
                    <div className={classes.marginTop1}>
                      <OutlinedInput
                        id="link-url"
                        style={{ height: 40 }}
                        placeholder={t('projectDocumentWorkflowApproval.placeholders.websiteUrl') as string}
                        type="url"
                        value={
                          userApproval.checklistLinkUrl
                            ? userApproval.checklistLinkUrl
                            : ''
                        }
                        onChange={(event) =>
                          setUserApproval({
                            ...userApproval,
                            checklistLinkUrl:
                              event.target.value === ''
                                ? null
                                : event.target.value
                          })
                        }
                        endAdornment={
                          userApproval.checklistLinkUrl && (
                            <InputAdornment position="end">
                              <Tooltip title={t('projectDocumentWorkflowApproval.tooltips.preview') as string} arrow>
                                <IconButton
                                  onClick={() => {
                                    if (userApproval.checklistLinkUrl)
                                      window.open(
                                        userApproval.checklistLinkUrl,
                                        '_blank'
                                      );
                                  }}
                                  edge="end"
                                >
                                  <LaunchRounded />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }
                        fullWidth
                      />
                    </div>
                    <FormControl
                      component="fieldset"
                      error={!userApproval.isChecked}
                      required
                    >
                      <FormControlLabel
                        className={classes.marginTop2}
                        control={
                          <Checkbox
                            color="primary"
                            checked={userApproval.isChecked}
                            onChange={(event) =>
                              setUserApproval({
                                ...userApproval,
                                isChecked: event.target.checked
                              })
                            }
                          />
                        }
                        label={
                          <span>
                            {t('projectDocumentWorkflowApproval.labels.qualityCheckConfirmation')}
                            <span className={classes.colorRed}> *</span>
                          </span>
                        }
                      />
                      {!userApproval.isChecked && (
                        <FormHelperText className={classes.formHelperText}>
                          * {t('projectDocumentWorkflowApproval.messages.pleaseCheckCheckbox')}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>
                  <DropzoneArea
                    dropzoneArea={dropzoneArea}
                    getRootProps={getRootChecklistProps}
                    getInputProps={getInputChecklistProps}
                    maxFileSizeInMB={MAX_SIZE_IN_MB}
                    inputKey={inputKey}
                    inDropzone={inDropzone}
                    disabled={loading}
                  />
                </Paper>
              )}
            {responseAnswer &&
              !responseAnswer.approvalTypeId &&
              workflowStatusId === WorkflowStatusLabel.InProgress && (
                <Paper className={classes.card}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('projectDocumentWorkflowApproval.sections.finishYourReview')}
                  </Typography>
                  <Divider className={classes.divider} />
                  <div
                    {...getRootProps({ className: 'dropzone' })}
                    className={clsx(
                      classes.container,
                      {
                        [classes.containerDisable]: loading
                      },
                      {
                        [classes.containerClearBorder]: commentFiles.length > 0
                      }
                    )}
                  >
                    <input {...getInputProps()} disabled={loading} />
                    <div className={classes.textCenter}>
                      <Typography variant="body2">
                        {t('projectDocumentWorkflowApproval.messages.dragAndDrop')}
                      </Typography>
                      <Typography
                        className={classes.cursorPointer}
                        variant="button"
                        color={loading ? 'textSecondary' : 'primary'}
                      >
                        <b>{t('projectDocumentWorkflowApproval.buttons.browse')}</b>
                      </Typography>
                      <div className={classes.textCaption}>
                        <Typography variant="caption">
                          {t('projectDocumentWorkflowApproval.labels.maximumSize')}: {MAX_SIZE_IN_MB} MB/{t('projectDocumentWorkflowApproval.labels.file')}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  {commentFiles.length > 0 && (
                    <div className={classes.fileList}>
                      {fileList(commentFiles, FileTypeLabel.Comment)}
                    </div>
                  )}
                  <TextField
                    className={clsx(classes.fullWidth, classes.marginTop2)}
                    id="comment"
                    variant="outlined"
                    size="small"
                    multiline
                    rowsMax={4}
                    placeholder={t('projectDocumentWorkflowApproval.placeholders.leaveComment') as string}
                    value={userApproval.comment ? userApproval.comment : ''}
                    onChange={(event) =>
                      setUserApproval({
                        ...userApproval,
                        comment:
                          event.target.value === '' ? null : event.target.value
                      })
                    }
                  />
                  {isFinalApprover && (
                    <div
                      className={clsx(
                        classes.flexAlignCenter,
                        classes.marginTop2
                      )}
                    >
                      <Typography
                        className={classes.approvalCodeText}
                        variant="body2"
                      >
                        {t('projectDocumentWorkflowApproval.labels.approvalCode')}
                      </Typography>
                      <Autocomplete
                        className={classes.fullWidth}
                        id="discipline"
                        options={approvalCodes.filter((approvalCode) =>
                          isIFIPurpose
                            ? approvalCode.id === ApprovalCodeLabel.Acknowledge
                            : approvalCode.id !== ApprovalCodeLabel.Acknowledge
                        )}
                        getOptionLabel={(option) => option?.name}
                        value={userApproval.approvalCode}
                        getOptionSelected={(option, value) =>
                          option.id === value?.id
                        }
                        onChange={(event, newValue) => {
                          if (newValue) {
                            const [{ id: approvalTypeId }] =
                              newValue.approvalTypes;
                            setUserApproval({
                              ...userApproval,
                              approvalCode: newValue,
                              approvalTypeId: approvalTypeId
                            });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={t('projectDocumentWorkflowApproval.placeholders.selectAnswer') as string}
                            required
                          />
                        )}
                      />
                    </div>
                  )}
                  <FormControl className={classes.formRadio} required>
                    <RadioGroup
                      aria-label="approval-type"
                      name="approval-type"
                      value={
                        userApproval.approvalTypeId
                          ? userApproval.approvalTypeId
                          : ''
                      }
                      onChange={(event) => {
                        setUserApproval({
                          ...userApproval,
                          approvalTypeId: Number.parseInt(
                            event.target.value,
                            10
                          )
                        });
                      }}
                    >
                      {approvalTypes
                        .filter((approvalType) => getApprovalType(approvalType))
                        .map((approvalType) => {
                          return (
                            <FormControlLabel
                              key={approvalType.id}
                              value={approvalType.id}
                              control={<Radio color="primary" />}
                              label={approvalType.name}
                            />
                          );
                        })}
                    </RadioGroup>
                  </FormControl>
                  <Button
                    className={classes.marginTop1}
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={
                      (needChecklist && !userApproval.isChecked) || loading
                    }
                  >
                    {!loading ? t('projectDocumentWorkflowApproval.buttons.submitReview') : t('projectDocumentWorkflowApproval.buttons.loading')}
                  </Button>
                </Paper>
              )}
          </form>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    checklistCard: {
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2),
      position: 'relative'
    },
    checklistFile: { minHeight: 64 },
    notFoundChecklistFile: {
      padding: theme.spacing(1, 2),
      textAlign: 'center'
    },
    formHelperText: {
      marginLeft: theme.spacing(4)
    },
    container: {
      color: theme.palette.text.hint,
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      borderRadius: theme.spacing(1),
      borderStyle: 'dashed',
      borderWidth: 'thin',
      padding: theme.spacing(2, 5, 1, 5),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 1, 0, 1)
      },
      transition: '0.4s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: fade(theme.palette.primary.main, 0.1)
      }
    },
    containerDisable: {
      '&:hover': {
        borderColor: '#dddddd',
        backgroundColor: fade(theme.palette.background.default, 0.75)
      }
    },
    containerClearBorder: {
      borderRadius: theme.spacing(1, 1, 0, 0)
    },
    fileList: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: theme.spacing(0.5, 0, 0.5, 2),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      borderRadius: theme.spacing(0, 0, 1, 1),
      borderStyle: 'solid',
      borderTopStyle: 'none',
      borderWidth: 'thin',
      '& > *': {
        marginRight: theme.spacing(1)
      },
      '&:last-child': {
        marginRight: theme.spacing(0)
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    smallAvatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      backgroundColor: theme.palette.primary.main
    },
    avatarDisabled: {
      backgroundColor: theme.palette.text.disabled
    },
    flexFlowStep: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(2, 0, 0, 0)
    },
    flexGrowName: {
      flexGrow: 1,
      padding: theme.spacing(0, 2)
    },
    userField: {
      width: `calc(100% - ${theme.spacing(8)}px)`,
      marginLeft: theme.spacing(6)
    },
    usernameVerified: {
      display: 'flex',
      alignItems: 'canter',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5)
    },
    checklistField: {
      display: 'flex',
      alignItems: 'canter',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      '& > *': {
        marginTop: 'auto',
        marginBottom: 'auto'
      }
    },
    truncatedChip: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%' // Adjust this to control the maximum width of the Chip
    },
    fileNotFound: {
      padding: theme.spacing(1, 2, 3, 2),
      textAlign: 'center'
    },
    hideBreadcrumb: {
      padding: theme.spacing(1.5, 0)
    },
    rotate45: {
      rotate: '-45deg'
    },
    greenColor: {
      color: theme.palette.success.main
    },
    marginIconButton: {
      margin: theme.spacing(-1, 0, -1, 1)
    },
    marginTop1: {
      marginTop: theme.spacing(1)
    },
    marginTop2: {
      marginTop: theme.spacing(2)
    },
    marginBottom1: {
      marginBottom: theme.spacing(1)
    },
    marginBottom2: {
      marginBottom: theme.spacing(2)
    },
    marginRight: {
      marginRight: theme.spacing(0.5)
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    },
    marginLR: {
      margin: theme.spacing(0, 1)
    },
    padding2: {
      padding: theme.spacing(2)
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    textCenter: {
      textAlign: 'center',
      margin: theme.spacing(1, 2)
    },
    textCaption: {
      marginTop: theme.spacing(2)
    },
    cursorPointer: {
      cursor: 'pointer'
    },
    flexGrow: {
      flexGrow: 1
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexDivider: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    filename: {
      wordBreak: 'break-word'
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.dark
      },
      wordBreak: 'break-all'
    },
    linkBox: {
      borderRadius: theme.spacing(1),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75)
    },
    linkNoteBox: {
      borderRadius: theme.spacing(1),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      padding: theme.spacing(0.8, 2)
    },
    commentFileList: {
      display: 'flex',
      flexWrap: 'wrap',
      wordBreak: 'break-word'
    },
    approvalCodeText: {
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      borderRadius: theme.spacing(1, 0, 0, 1),
      borderStyle: 'none',
      height: 40,
      textAlign: 'right',
      lineHeight: '100%',
      padding: theme.spacing(0.75, 1.5, 0, 0)
    },
    formRadio: {
      margin: 8,
      width: '100%'
    },
    clearMarginForMobile: {
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(-2)
      }
    },
    colorRed: {
      color: theme.palette.error.main
    }
  })
);
